export interface Volume {
  number: string
  title: string
  subtitle: string
  intro: string[]
  questions?: string[]
  highlight?: string
  afterHighlight?: string
  bulletsIntro: string
  bullets: string[]
  sequenceIntro?: string
  sequence?: string
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  resultLead?: string
  resultQuote?: string
  result: string
  resultEmphasis?: string
  popular?: boolean
}

export const volumes: Volume[] = [
  {
    number: 'Volume 1',
    title: 'Flight Foundations, Cockpit Familiarization & First Lessons',
    subtitle: 'Build the foundation every new student pilot needs.',
    intro: [
      'A new student needs more than aviation information.',
      'They need a clear progression that takes them from cockpit familiarization toward real aircraft control and confidence.',
      'This volume gives the instructor a structured, 100% visual way to introduce the fundamentals instead of rebuilding every beginner lesson.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Cockpit familiarization',
      'Aircraft controls',
      'Primary instruments',
      'Pre-flight concepts',
      'Taxi fundamentals',
      'Straight-and-level flight',
      'First climbs',
      'First descents',
      'Basic turns',
      'Control coordination',
      'Trim fundamentals',
      'Student errors',
      'Instructor cues',
      'Foundational progressions',
      'First-lesson structures',
    ],
    result:
      'Your student develops stronger foundations while you follow a clear, visual and repeatable teaching progression.',
  },
  {
    number: 'Volume 2',
    title: 'Aircraft Control, Aerodynamics & Core Flight Maneuvers',
    subtitle: 'Turn flight-control concepts into clear visual teaching sequences.',
    intro: [
      'Students need to understand more than what the aircraft is doing.',
      'They need to understand why it is happening and how their control inputs affect the result.',
    ],
    bulletsIntro: 'This volume helps you teach:',
    bullets: [
      'Pitch',
      'Power',
      'Trim',
      'Aircraft attitude',
      'Coordinated turns',
      'Climbs',
      'Descents',
      'Slow flight',
      'Stall awareness',
      'Core maneuvers',
      'Steep-turn concepts',
      'Control relationships',
      'Common errors',
      'Visual references',
      'Instructor corrections',
    ],
    result:
      'Students begin understanding aircraft behavior more clearly because they can see the relationship between control input, aircraft response and desired result.',
  },
  {
    number: 'Volume 3',
    title: 'Takeoff, Traffic Patterns, Approach & Landing',
    subtitle: 'Make one of the most important stages of flight training easier to explain visually.',
    intro: [
      'Takeoffs, traffic patterns, approaches and landings involve several decisions and actions happening in sequence.',
      'Explaining everything verbally can quickly overwhelm developing students.',
      'This volume breaks the process down into clear, 100% visual teaching sequences.',
    ],
    bulletsIntro: 'Includes:',
    bullets: [
      'Takeoff preparation',
      'Takeoff sequence',
      'Climb-out',
      'Traffic patterns',
      'Pattern positioning',
      'Approach setup',
      'Stabilized approach concepts',
      'Alignment',
      'Round-out',
      'Flare',
      'Touchdown',
      'Directional control',
      'Go-around decision-making',
      'Landing errors',
      'Corrections',
      'Instructor cues',
      'Training drills',
    ],
    result: 'Students can see the sequence before attempting it in the aircraft.',
  },
  {
    number: 'Volume 4',
    title: 'Navigation, Radio Communication & Flight Planning',
    subtitle: 'Give your students a clearer system for planning, navigating and communicating.',
    intro: [
      'Navigation and communication introduce additional workload.',
      'Your student must fly the aircraft while also planning, observing, communicating and making decisions.',
      'This volume organizes those skills into visual and practical training sequences.',
    ],
    bulletsIntro: 'Includes:',
    bullets: [
      'Charts',
      'Route planning',
      'Checkpoints',
      'Heading concepts',
      'Time concepts',
      'Situational awareness',
      'Radio communication frameworks',
      'Navigation practice',
      'Flight planning',
      'Diversion concepts',
      'Workload management',
      'Student errors',
      'Instructor cues',
      'Training progressions',
    ],
    result:
      'Students develop more organized navigation and communication habits while you teach from a structured visual system.',
  },
  {
    number: 'Volume 5',
    title: 'Weather, Risk Management & Emergency Decision-Making',
    subtitle: 'Help students develop stronger awareness, judgment and decision-making.',
    intro: [
      'Aircraft control alone does not create a capable pilot.',
      'Students also need to learn how to recognize changing conditions, assess risk and make better decisions.',
    ],
    bulletsIntro: 'Includes:',
    bullets: [
      'Weather awareness',
      'Wind',
      'Visibility',
      'Turbulence',
      'Environmental considerations',
      'Risk assessment',
      'Situational awareness',
      'Decision-making',
      'Abnormal situations',
      'Emergency priorities',
      'Instructor intervention',
      'Student judgment',
      'Scenario discussions',
      'Common mistakes',
      'Visual decision frameworks',
    ],
    result:
      'Students begin progressing from simply controlling the aircraft toward making more disciplined and independent decisions.',
  },
  {
    number: 'Volume 6',
    title: '150 Ready-to-Teach Flight Lessons — Beginner to Checkride-Ready',
    subtitle: 'Stop Building Every Flight Lesson From Scratch.',
    intro: ['This is the practical heart of the complete 6-in-1 system.', 'You receive:'],
    highlight: '150 COMPLETE READY-TO-TEACH FLIGHT LESSONS',
    afterHighlight: 'Organized so you can open the system, choose the objective and begin teaching.',
    bulletsIntro: 'Lesson areas include:',
    bullets: [
      'Flight foundations',
      'Cockpit familiarization',
      'Aircraft control',
      'Straight-and-level flight',
      'Climbs',
      'Descents',
      'Turns',
      'Slow flight',
      'Takeoffs',
      'Traffic patterns',
      'Approaches',
      'Landings',
      'Go-arounds',
      'Navigation',
      'Radio communication',
      'Flight planning',
      'Weather',
      'Risk management',
      'Decision-making',
      'Assessment',
      'Checkride preparation',
    ],
    secondaryBulletsIntro: 'Lessons can include:',
    secondaryBullets: [
      'Objective',
      'Student level',
      'Setup',
      'Visual explanation',
      'Instructor demonstration',
      'Student practice',
      'What to observe',
      'Instructor cues',
      'Common errors',
      'Correction',
      'Easier variation',
      'Progression',
      'Success check',
    ],
    resultLead: 'Instead of asking:',
    resultQuote: '“What am I going to teach this student today?”',
    result: 'You open Flight Instructor Ready and:',
    resultEmphasis: 'YOU ALREADY HAVE THE LESSON.',
    popular: true,
  },
]
